import React, { useState, useEffect, useContext } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Restore } from '@mui/icons-material';
import axiosInstance from '../../api';
import { authContext } from '../../routes';
import StyledCard from '../../components/card/StyledCard';
import SERVER_API_URL from '../../config';
import LocaleText from '../../utils/localText';
import RestoreDialog from '../common/RestoreDialog';
import CustomSnackbar from '../../components/alerts/CustomSnackbar';
import { ethDate } from '../../utils/ethDate';

export default function DeletedFeedbacks() {
  const { user } = useContext(authContext);
  const [feedbackList, setFeedbackList] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState();
  const [notification, setNotification] = useState({});
  const [openRestore, setOpenRestore] = useState(false);

  const columns = [
    { headerClassName: 'super-app-theme--header', field: 'name', width: 250, headerName: 'የላኪ ስም' },
    {
      headerClassName: 'super-app-theme--header', field: 'email',
      width: 300,
      headerName: 'ኢሜል',
    },
    { headerClassName: 'super-app-theme--header', field: 'subject', width: 200, headerName: 'ርዕሰ ጉዳይ' },
    { headerClassName: 'super-app-theme--header', field: 'message', width: 200, headerName: 'መልዕክት' },
    {
      headerClassName: 'super-app-theme--header', field: 'status',
      width: 100,
      headerName: 'ሁኔታ',
      renderCell: (params) => (
        <Typography
          sx={{
            ml: 'auto',
            pr: 1,
            pl: 1,
            mr: 5,
            backgroundColor:
              params.value === 'አልታየም' ? (theme) => theme.palette.grey[700] : (theme) => theme.palette.grey[500],
            borderRadius: 2,
            height: 25,
            width: 100,
          }}
          textAlign="center"
        >
          {params.value}
        </Typography>
      ),
    },
    {
      headerClassName: 'super-app-theme--header', field: 'action',
      width: 100,
      headerName: 'ድርጊቶች',
      renderCell: (params) => {
        const handleDelete = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/feedback/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedFeedback(response.data);
              setOpenDelete(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };
        const handleRestore = async () => {
          try {
            const response = await axiosInstance.post(`${SERVER_API_URL}/feedback/findOneById/${params.id}`);
            if (response.status === 200) {
              setSelectedFeedback(response.data);
              setOpenRestore(true);
            }
          } catch (error) {
            // console.log(error)
          }
        };
        return (
          <>
            <Tooltip title="ሰርዝ" color="error" arrow>
              <IconButton disabled={!(user && user.role.permissions.contact.delete)} onClick={handleDelete}>
                <Delete sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="ፋይሉን መልስ" color="success" arrow>
              <IconButton disabled={!(user && user.role.permissions.contact.delete)} onClick={handleRestore}>
                <Restore sx={{ fontSize: '18px' }} />
              </IconButton>
            </Tooltip>
          </>
        );
      },
    },
    {
      headerClassName: 'super-app-theme--header', field: 'createdAt',
      headerName: 'የተላከበት ቀን',
      width: 200,
      valueGetter: (params) => ethDate(params.value),
    },
    {
      headerClassName: 'super-app-theme--header', field: 'updatedAt',
      headerName: 'የታየበት ቀን',
      width: 200,
      valueGetter: (params) => ethDate(params.value),
    },
  ];

  const handleDeleteOk = async () => {
    try {
      const response = await axiosInstance.delete(`${SERVER_API_URL}/feedback/deleteOneById/${selectedFeedback.id}`);
      if (response.status === 200) {
        getAllFeedback();
        setOpenDelete(false);
        notification.message = 'መለዕክት በተሳካ ሁኔታ ሰርዘዋል።';
        notification.severity = 'success';
      }
    } catch (error) {
      // console.log(error)
    }
  };
  const handleRestoreOk = async () => {
    try {
      const response = await axiosInstance.patch(`${SERVER_API_URL}/feedback/restoreOneById/${selectedFeedback.id}`);
      if (response.status === 200) {
        getAllFeedback();
        setOpenRestore(false);
        notification.message = 'መለዕክት በተሳካ ሁኔታ መልሰዋል።';
        notification.severity = 'success';
      }
    } catch (error) {
      if (error.response.status === 406) {
        notification.message = 'በዚህ መለዕክቶች ስም የተመዘገበ አለ።';
        notification.severity = 'error';
        setOpenRestore(false);
      }
    }
  };
  const getAllFeedback = async () => {
    try {
      const response = await axiosInstance.post(`${SERVER_API_URL}/feedback/findAllDeleted`);

      if (response.status === 200) {
        setFeedbackList(response.data);
      }
    } catch (error) {
      // console.log(error);
    }
  };

  useEffect(() => {
    getAllFeedback();
  }, []);

  return (
    <>
      <StyledCard sx={{
        '& .super-app-theme--header': {
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? theme.palette.primary.darkMode.lighter : theme.palette.grey[400],
        }
      }}>
        {notification.message && <CustomSnackbar notification={notification} setNotification={setNotification} />}
        <DataGrid
          sx={{ padding: 2, '& .MuiDataGrid-row': { backgroundColor: (theme) => theme.palette.mode === 'dark' ? "" : theme.palette.error.lighter, borderWidth: 0.5, borderTopColor: (theme) => theme.palette.error.darker, borderTopStyle: 'dashed' } }}
          rows={feedbackList}
          columns={columns}
          pageSize={12}
          initialState={{
            filter: {
              filterModel: {
                items: [],
                quickFilterValues: [''],
              },
            },
            pagination: { pageSize: 10 },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          disableSelectionOnClickCell
          disableColumnFilter
          localeText={LocaleText.pagination}
        />
      </StyledCard>
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <Box sx={{ backgroundColor: (theme) => theme.palette.mode === 'light' ? '#F9F9FB' : '#272424' }}>
          <DialogTitle>እርግጠኛ ነህ የ{selectedFeedback && selectedFeedback.name} መልዕክትን መሰረዝ ትፈልጋለህ?</DialogTitle>
          <DialogContent>
            <DialogContentText>በዚህ ምድብ ስር ያሉ ሁሉም ተጠቃሚዎች ይጠፋሉ!</DialogContentText>
            <DialogContentText>ይህ እርምጃ ሊቀለበስ አይችልም።</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDelete(false)}>ተመለስ</Button>
            <Button color="error" variant="contained" onClick={handleDeleteOk} autoFocus>
              ሰርዝ
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {selectedFeedback && (
        <RestoreDialog
          openRestore={openRestore}
          setOpenRestore={setOpenRestore}
          nameToBeRestored={selectedFeedback.name.slice(0, selectedFeedback.name.length - selectedFeedback.id.length)}
          nameType="መለዕክቶች"
          handleRestoreOk={handleRestoreOk}
          id={selectedFeedback.id}
        />
      )}
    </>
  );
}
